import React, { memo, useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { mapOptionsListStyleToCSS } from '../../../../utils/styleMapper';
import { OptionsItemText } from '../OptionsItemText';
import type { OptionsItemVariantProps } from './types';
import { getOptionItemA11yProps } from '../../../../utils/optionItemA11y';
import styles from '../OptionsItem.module.css';
import type { LocalizedLabel } from '@segmently/schema-models/v2';

/** Strip appearance.insets from label to prevent hidden padding not controlled by UI */
function stripAppearanceInsets(label: LocalizedLabel | undefined): LocalizedLabel | undefined {
  if (!label?.appearance || !(label.appearance as Record<string, unknown>).insets) return label;
  const { insets: _, ...restAppearance } = label.appearance as Record<string, unknown>;
  return { ...label, appearance: restAppearance as LocalizedLabel['appearance'] };
}

export const TitleSubtitle = memo(function TitleSubtitle({
  item,
  index,
  locale,
  variables,
  selectable,
  selected,
  disabled,
  selectionMode,
  appearance,
  selectedAppearance,
  onSelect,
  className,
  style,
  'data-testid': testId,
  editPath,
  columnLayout,
}: OptionsItemVariantProps): React.ReactElement {
  const handleClick = useCallback(() => {
    if (selectable && !disabled && onSelect) {
      onSelect(item, index, !selected);
    }
  }, [selectable, disabled, onSelect, item, index, selected]);

  const isGrid = columnLayout === 'twoCols';

  const activeStyle = selected ? selectedAppearance : appearance;
  const itemStyle: CSSProperties = mapOptionsListStyleToCSS(activeStyle);

  // In grid mode, strip appearance.insets from labels — padding only from layout.insets (UI-controlled)
  const titleLabel = useMemo(
    () => isGrid ? stripAppearanceInsets(item.title) : item.title,
    [isGrid, item.title]
  );
  const subtitleLabel = useMemo(
    () => isGrid ? stripAppearanceInsets(item.subtitle) : item.subtitle,
    [isGrid, item.subtitle]
  );

  return (
    <div
      className={`${styles.listItem}${isGrid ? ` ${styles.listItemGrid}` : ''}${selected ? ` ${styles.selected}` : ''}${disabled ? ` ${styles.disabled}` : ''}${className ? ` ${className}` : ''}`}
      style={style ? { ...itemStyle, ...style } : itemStyle}
      onClick={selectable && !disabled ? handleClick : undefined}
      {...getOptionItemA11yProps({ selectable, selected, disabled, selectionMode, onActivate: handleClick })}
      data-testid={testId}
      data-edit-path={editPath}
    >
      <div className={styles.content}>
        <OptionsItemText label={titleLabel} locale={locale} variables={variables} editPath={editPath ? `${editPath}.title` : undefined} />
        <OptionsItemText label={subtitleLabel} locale={locale} variables={variables} editPath={editPath ? `${editPath}.subtitle` : undefined} />
      </div>
    </div>
  );
});

TitleSubtitle.displayName = 'TitleSubtitle';
